import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function KotKitchenStationMaster({ token, selectedProperty, selectedOutletId = '' }) {
  const [outlets, setOutlets] = useState([]);
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || '',
    outlet_id: selectedOutletId || ''
  });
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', printer_name: '', kds_screen_code: '', sla_minutes: '', is_active: true });
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id,
    'x-outlet-id': scope.outlet_id
  });

  const loadOutlets = async (chainId, propertyId) => {
    if (!chainId || !propertyId) return;
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load outlets');
      const all = Array.isArray(data) ? data : [];
      // Filter out Bar-only outlets — KOT stations are managed separately for Bar
      const list = all.filter(o => String(o.outlet_type || '').toUpperCase() !== 'BAR' || o.module_kot_enabled === true);
      setOutlets(list);
      if (list.length) {
        const match = selectedOutletId && list.find((o) => String(o.id) === String(selectedOutletId));
        setScope((prev) => ({ ...prev, outlet_id: match ? match.id : list[0].id }));
      } else setScope((prev) => ({ ...prev, outlet_id: '' }));
    } catch (err) {
      setError(err?.message || 'Network error while loading outlets');
    }
  };

  const loadRows = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setRows([]);
    try {
      const res = await fetch('/api/v1/kot/masters/stations?all=1', { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load kitchen stations');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading kitchen stations');
    }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id, outlet_id: selectedOutletId || '' }));
    } else {
      setScope({ chain_id: '', property_id: '', outlet_id: '' });
      setRows([]);
      setOutlets([]);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedOutletId && outlets.some((o) => String(o.id) === String(selectedOutletId))) {
      setScope((prev) => ({ ...prev, outlet_id: selectedOutletId }));
    }
  }, [selectedOutletId]);

  useEffect(() => { if (scope.chain_id && scope.property_id) loadOutlets(scope.chain_id, scope.property_id); }, [scope.chain_id, scope.property_id, token]);
  useEffect(() => { loadRows(); }, [scope.outlet_id]);

  const saveRow = async () => {
    setError('');
    if (!scope.chain_id || !scope.property_id) return setError('Select property first');
    if (!scope.outlet_id) return setError('Select outlet first');
    if (!String(form.name || '').trim()) return setError('Station name is required');
    try {
      const res = await fetch(editingId ? `/api/v1/kot/masters/stations/${editingId}` : '/api/v1/kot/masters/stations', {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          printer_name: form.printer_name || null,
          kds_screen_code: form.kds_screen_code || null,
          sla_minutes: form.sla_minutes === '' ? null : Number(form.sla_minutes),
          is_active: form.is_active !== false
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Kitchen station updated successfully' : 'Kitchen station created successfully');
      setEditingId('');
      setForm({ name: '', printer_name: '', kds_screen_code: '', sla_minutes: '', is_active: true });
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      printer_name: row.printer_name || '',
      kds_screen_code: row.kds_screen_code || '',
      sla_minutes: row.sla_minutes ?? '',
      is_active: row.is_active !== false
    });
    setError('');
    setMessage('');
    scrollToForm();
  };

  const deactivateRow = async (id, name) => {
    if (!window.confirm(`Deactivate kitchen station "${name}"?`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/kot/masters/stations/${id}`, { method: 'DELETE', headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Deactivate failed');
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Kitchen Station Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Property & Outlet</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.7rem' }}>
          <input
            value={selectedProperty ? `${selectedProperty.code || ''} ${selectedProperty.name || ''}`.trim() : 'No property selected'}
            readOnly
            style={{ ...inputStyle, background: '#ecf0f1' }}
          />
          <select value={scope.outlet_id} onChange={(e) => setScope((prev) => ({ ...prev, outlet_id: e.target.value }))} style={inputStyle}>
            <option value="">Select Outlet</option>
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.code} - {o.name}</option>)}
          </select>
        </div>
      </div>

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '1rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Kitchen Station' : 'Add Kitchen Station'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Station Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          <input placeholder="Printer Name (optional)" value={form.printer_name} onChange={(e) => setForm({ ...form, printer_name: e.target.value })} style={inputStyle} />
          <input placeholder="KDS Screen Code (optional)" value={form.kds_screen_code} onChange={(e) => setForm({ ...form, kds_screen_code: e.target.value })} style={inputStyle} />
          <input type="number" min="0" placeholder="Prep SLA (min)" title="Baseline prep time (raised → ready). Blank = no SLA." value={form.sla_minutes} onChange={(e) => setForm({ ...form, sla_minutes: e.target.value })} style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={saveRow} className="btn-primary" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{editingId ? 'Update' : 'Add'}</button>
          {editingId && (
            <button onClick={() => { setEditingId(''); setForm({ name: '', printer_name: '', kds_screen_code: '', sla_minutes: '', is_active: true }); }} className="btn-muted" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <h3>Kitchen Stations</h3>
        {rows.length === 0 ? (
          <p>No stations found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Printer</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>KDS Code</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Prep SLA</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.printer_name || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.kds_screen_code || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.sla_minutes != null ? `${r.sla_minutes} min` : '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => editRow(r)} style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deactivateRow(r.id, r.name)} className="btn-danger" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default KotKitchenStationMaster;

